import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, BookOpen, UserPlus, MessageSquare, TrendingUp, ArrowRight, AlertCircle, UserCheck, CheckSquare, BarChart3 } from 'lucide-react';
import { DashboardStats } from '../../types';
import api from '../../services/api';

export default function AdminDashboard() {
  const { t } = useTranslation('admin');
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get('/admin/dashboard');
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch admin stats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: t('dashboard.totalUsers'),
      value: stats?.totalUsers || 0,
      icon: Users,
      color: 'blue',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700',
    },
    {
      title: t('dashboard.totalCourses'),
      value: stats?.totalCourses || 0,
      icon: BookOpen,
      color: 'green',
      bgColor: 'bg-green-50',
      textColor: 'text-green-700',
    },
    {
      title: t('dashboard.activeEnrollments'),
      value: stats?.totalEnrollments || 0,
      icon: UserPlus,
      color: 'purple',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600',
    },
    {
      title: t('dashboard.newContacts'),
      value: stats?.newContacts || 0,
      icon: MessageSquare,
      color: 'orange',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-600',
    },
  ];

  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center space-x-2 mb-2">
          <TrendingUp className="w-6 h-6 text-red-600" />
          <h1 className="font-display text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>
        </div>
        <p className="text-gray-500">{t('dashboard.subtitle')}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat) => (
          <div key={stat.title} className="glass-card rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">{stat.title}</p>
                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
              </div>
              <div className={`w-14 h-14 rounded-2xl ${stat.bgColor} flex items-center justify-center`}>
                <stat.icon className={`w-7 h-7 ${stat.textColor}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pending Items */}
      {((stats?.pendingCertifiedTrainerApplications || 0) > 0 || (stats?.pendingCourses || 0) > 0) && (
        <div className="glass-card rounded-2xl border border-yellow-500/30 p-6 mb-8">
          <div className="flex items-center space-x-2 mb-6">
            <AlertCircle className="w-5 h-5 text-yellow-400" />
            <h2 className="text-lg font-semibold text-gray-900">{t('dashboard.pendingApprovals')}</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {(stats?.pendingCertifiedTrainerApplications || 0) > 0 && (
              <Link
                to="/admin/trainers/applications"
                className="group flex items-center justify-between p-4 bg-yellow-500/10 rounded-xl border border-yellow-500/20 hover:border-yellow-500/50 transition-all"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                    <UserCheck className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 group-hover:text-yellow-400 transition-colors">
                      {t('dashboard.certifiedTrainerApplications')}
                    </p>
                    <p className="text-sm text-gray-500">
                      {t('dashboard.pendingReview', { count: stats?.pendingCertifiedTrainerApplications })}
                    </p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            )}
            {(stats?.pendingCourses || 0) > 0 && (
              <Link
                to="/admin/courses/approvals"
                className="group flex items-center justify-between p-4 bg-yellow-500/10 rounded-xl border border-yellow-500/20 hover:border-yellow-500/50 transition-all"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                    <CheckSquare className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 group-hover:text-yellow-400 transition-colors">
                      {t('dashboard.courseApprovals')}
                    </p>
                    <p className="text-sm text-gray-500">
                      {t('dashboard.pendingReview', { count: stats?.pendingCourses })}
                    </p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="glass-card rounded-2xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">{t('dashboard.quickActions')}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Link
            to="/admin/courses"
            className="group p-5 bg-gray-50 rounded-xl border border-gray-200 hover:border-green-500/50 transition-all"
          >
            <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-green-500/30 transition-colors">
              <BookOpen className="w-6 h-6 text-green-600" />
            </div>
            <p className="font-medium text-gray-900 mb-1 group-hover:text-green-600 transition-colors flex items-center">
              {t('dashboard.manageCourses')}
              <ArrowRight className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
            </p>
            <p className="text-sm text-gray-500">{t('dashboard.addOrEditCourses')}</p>
          </Link>
          <Link
            to="/admin/users"
            className="group p-5 bg-gray-50 rounded-xl border border-gray-200 hover:border-blue-500/50 transition-all"
          >
            <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-500/30 transition-colors">
              <Users className="w-6 h-6 text-blue-700" />
            </div>
            <p className="font-medium text-gray-900 mb-1 group-hover:text-blue-400 transition-colors flex items-center">
              {t('dashboard.manageUsers')}
              <ArrowRight className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
            </p>
            <p className="text-sm text-gray-500">{t('dashboard.viewAndManageUsers')}</p>
          </Link>
          <Link
            to="/admin/contacts"
            className="group p-5 bg-gray-50 rounded-xl border border-gray-200 hover:border-orange-500/50 transition-all"
          >
            <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-orange-500/30 transition-colors">
              <MessageSquare className="w-6 h-6 text-orange-600" />
            </div>
            <p className="font-medium text-gray-900 mb-1 group-hover:text-orange-400 transition-colors flex items-center">
              {t('dashboard.contactRequests')}
              <ArrowRight className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
            </p>
            <p className="text-sm text-gray-500">{t('dashboard.handleInquiries')}</p>
          </Link>
          <Link
            to="/admin/enrollments"
            className="group p-5 bg-gray-50 rounded-xl border border-gray-200 hover:border-purple-500/50 transition-all"
          >
            <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-purple-500/30 transition-colors">
              <UserPlus className="w-6 h-6 text-purple-600" />
            </div>
            <p className="font-medium text-gray-900 mb-1 group-hover:text-purple-400 transition-colors flex items-center">
              {t('dashboard.enrollments')}
              <ArrowRight className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
            </p>
            <p className="text-sm text-gray-500">{t('dashboard.manageEnrollments')}</p>
          </Link>
          <Link
            to="/admin/analytics"
            className="group p-5 bg-gray-50 rounded-xl border border-gray-200 hover:border-gold/50 transition-all"
          >
            <div className="w-12 h-12 bg-gold/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-gold/30 transition-colors">
              <BarChart3 className="w-6 h-6 text-gold" />
            </div>
            <p className="font-medium text-gray-900 mb-1 group-hover:text-gold transition-colors flex items-center">
              {t('dashboard.analytics')}
              <ArrowRight className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
            </p>
            <p className="text-sm text-gray-500">{t('dashboard.viewStatistics')}</p>
          </Link>
        </div>
      </div>
    </div>
  );
}
